import React, { useMemo, useState } from "react";
import { Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  useMyAvailability,
  useCreateAvailability,
  useDeleteAvailability,
} from "../hooks/useAvailability";

// Orden de semana empezando en lunes; value = convención JS getDay() (0=domingo).
const DAYS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
  { value: 0, label: "Domingo" },
];

const hhmm = (t) => (t ? String(t).slice(0, 5) : "");

/**
 * Gestión de la disponibilidad semanal del docente (D8). Por cada día se pueden
 * añadir/eliminar franjas horarias; el calendario sombrea las horas fuera de
 * disponibilidad.
 */
function AvailabilityModal({ show, onHide }) {
  const { data: windows = [], isLoading } = useMyAvailability();
  const createMut = useCreateAvailability();
  const deleteMut = useDeleteAvailability();
  const [drafts, setDrafts] = useState({}); // { [weekday]: { start, end } }

  const byDay = useMemo(() => {
    const m = {};
    windows.forEach((w) => {
      (m[w.weekday] ??= []).push(w);
    });
    Object.values(m).forEach((list) =>
      list.sort((a, b) => a.start_time.localeCompare(b.start_time))
    );
    return m;
  }, [windows]);

  const setDraft = (weekday, field, value) =>
    setDrafts((prev) => ({
      ...prev,
      [weekday]: { ...prev[weekday], [field]: value },
    }));

  const addWindow = (weekday) => {
    const d = drafts[weekday] || {};
    if (!d.start || !d.end) {
      toast.error("Indica la hora de inicio y de fin.");
      return;
    }
    if (d.start >= d.end) {
      toast.error("La hora de fin debe ser mayor que la de inicio.");
      return;
    }
    createMut.mutate(
      { weekday, start_time: d.start, end_time: d.end },
      {
        onSuccess: () =>
          setDrafts((prev) => ({ ...prev, [weekday]: { start: "", end: "" } })),
        onError: (err) =>
          toast.error(
            err?.response?.data?.field_errors?.end_time?.[0] ??
              "No se pudo añadir la franja."
          ),
      }
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Mi disponibilidad</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          Define las franjas en las que estás disponible cada día. En las vistas
          Semana y Día del calendario, las horas fuera de tu disponibilidad se
          muestran sombreadas. Si no defines ninguna, no se sombrea nada.
        </p>

        {isLoading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {DAYS.map((day) => {
              const list = byDay[day.value] ?? [];
              const draft = drafts[day.value] || {};
              return (
                <div key={day.value} className="border-bottom pb-3">
                  <div className="fw-medium mb-2">{day.label}</div>
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {list.length === 0 && (
                      <span className="text-muted small">Sin disponibilidad.</span>
                    )}
                    {list.map((w) => (
                      <Badge
                        key={w.id}
                        bg="light"
                        text="dark"
                        className="border d-inline-flex align-items-center gap-2 py-2"
                      >
                        {hhmm(w.start_time)}–{hhmm(w.end_time)}
                        <button
                          type="button"
                          className="btn-close"
                          style={{ fontSize: ".6rem" }}
                          aria-label={`Eliminar franja ${hhmm(w.start_time)}–${hhmm(
                            w.end_time
                          )}`}
                          onClick={() => deleteMut.mutate(w.id)}
                        />
                      </Badge>
                    ))}
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Form.Control
                      type="time"
                      size="sm"
                      value={draft.start || ""}
                      onChange={(e) => setDraft(day.value, "start", e.target.value)}
                      style={{ maxWidth: 130 }}
                      aria-label={`${day.label} desde`}
                    />
                    <span className="text-muted small">a</span>
                    <Form.Control
                      type="time"
                      size="sm"
                      value={draft.end || ""}
                      onChange={(e) => setDraft(day.value, "end", e.target.value)}
                      style={{ maxWidth: 130 }}
                      aria-label={`${day.label} hasta`}
                    />
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => addWindow(day.value)}
                      disabled={createMut.isPending}
                    >
                      <i className="mdi mdi-plus" aria-hidden="true" /> Añadir
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} style={{ minHeight: 44 }}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export { AvailabilityModal };
